import TenantConfigService from "@/services/TenantConfigService";

class TenantConfigLoader{
    #env = "";
    #configMap;
    #isUtilHelpersLoaded = false;
    #utilHelpers = {};
    #mapType = "base";
    #sKey = "";

    async _init(){
        this.#setSKey();
        await this.#getUtilHelpers();
    }

    /**
     * Loads application configuration
     * @async
     * @param {string} Auth0Token - The Auth0 authentication token.
     * @param {string} ACTIVE_TENANT_ID - The ID of the active tenant.
     * @param {boolean} [getPersona=false] - Optional flag to include persona information in the configuration.
     * @returns {Promise<Object>} A frozen configuration object containing either base or OS-specific configuration.
     */
    async appConfigLoader( Auth0Token, ACTIVE_TENANT_ID, getPersona = false ){
        TenantConfigService.setTenantIds( { tenantId: ACTIVE_TENANT_ID } );
        TenantConfigService.setHeaders( Auth0Token );

        const configMap = this.#mapType === "base" ?
            this.#mainConfigLoader( ACTIVE_TENANT_ID, getPersona ) :
            this.#osConfigLoader( ACTIVE_TENANT_ID, getPersona );

        return Object.freeze( configMap );
    }

    /**
     * Clear config cache
     */
    async clearCacheConfig(){
        if ( localStorage.getItem( this.#sKey ) ){

            localStorage.removeItem( this.#sKey );
        }

    }

    /**
     * Loads and configures authentication settings for a tenant.
     *
     * Retrieves tenant authentication configuration from TenantConfigService and uses it to
     * populate the global configMap object with tenant-specific settings including active
     * tenant information, build type, authentication configuration, and tenant identifiers.
     *
     * @async
     * @method initialConfigLoader
     * @returns {Promise<Object>} The configured configMap object containing tenant and auth settings
     * @throws {Error} May throw if TenantConfigService.getTenantAuthConfig() fails
     */
    async initialConfigLoader(){
        // Check cache for existing configuration before making API call
        const cachedConfig = await this.#cacheCheck( this.#sKey );

        // If a valid cached configuration exists, return it immediately
        if ( cachedConfig?.AUTH_CONFIG ){
            return cachedConfig;
        }

        const tenantConfig = await TenantConfigService.getTenantAuthConfig();
        const {
            authConfig: {
                clientId,
                customDomain,
                tenantId,
                tenantName,
                images,
                siteSettings,
                theme
            },
            env
        } = tenantConfig;

        const configMap = await this.#getConfigMap( tenantId );

        this.#env = env;
        configMap.CONFIG = {
            ACTIVE_TENANT: tenantName,
            ACTIVE_TENANT_ID: Number( tenantId ),
            BUILD_TYPE: env,
            S_KEY: this.#sKey
        };
        configMap.AUTH_CONFIG = this.#buildAuthConfig( {
            audience: "https://tsunami-api-endpoint",
            clientId,
            customDomain
        } );

        if ( siteSettings ){
            configMap.DETAILS = {
                pageTitle: siteSettings.pageTitle || ""
            };
            configMap.LINKS = {
                ABOUT_LINK: siteSettings.aboutLink || "",
                HELP_LINK: siteSettings.helpLink || "",
                PRIVACY_LINK: siteSettings.privacyLink || "",
                T_AND_C_LINK: siteSettings.termsAndConditionsLink || ""
            };
        }

        if ( images ){
            configMap.IMAGES = {
                defaultPerkIcon: images.perk || "",
                email: images.email || images.main || "",
                favicon: images.favicon || "",
                logo: images.main || "",
                navlogo: images.nav || ""
            };
        }

        if ( theme ){
            configMap.THEME = theme;
        }

        configMap.TENANT = tenantName;
        configMap.TENANT_ID = tenantId;

        return configMap;
    }

    /**
     * Builds an authentication configuration object for Auth0 integration.
     *
     * @private
     * @param {Object} params - The authentication parameters
     * @returns {string} returns.clientId - The Auth0 client ID (duplicate for convenience)
     */
    #buildAuthConfig( { audience, clientId, customDomain } ){
        return {
            authConfig: {
                authorizationParams: { audience },
                cacheLocation: "memory",
                clientId,
                domain: customDomain,
                scope: "openid offline_access profile email",
                useRefreshTokens: true,
                useRefreshTokensFallback: true
            },
            clientId
        };
    }

    /**
     * Checks the cache stored storage under the private key and returns the decoded
     * configuration if present and not expired.
     *
     * If the cached entry has an expiration (`e`) that has passed, the entry is removed and `null` is returned.
     * If a cached config is present, it is decoded via `this.#utilHelpers.cipher.decode` and returned.
     * @private
     * @returns {Promise<null|any>} The decoded config object, or `null` if missing/expired/invalid.
     */
    async #cacheCheck(){
        const itemStr = localStorage.getItem( this.#sKey );

        if ( !itemStr ){
            return null;
        }

        const item = JSON.parse( itemStr );
        const { e, v: config } = item;

        if ( e && Date.now() > e ){
            localStorage.removeItem( this.#sKey );

            return null;
        }

        if ( config ){
            // If a config is found and is not expired, decode it and return the decoded config
            const decodedConfig = await this.#utilHelpers.cipher.decode( config );

            return decodedConfig;
        }

        return null;
    }

    /**
     * Stores an config to cache with an expiration timestamp.
     * @param {*} config - The configuration data to cache.
     * @param {number} [mins=30] - The number of minutes before the cache entry expires.
     */
    async #cacheSet( config, mins = 30 ){
        const expireTTL = 60000 * mins;

        if ( config ){
            // Encode the config and store it in localStorage with an expiration time
            const encodeConfig = await this.#utilHelpers.cipher.encode( config );
            const item = {
                e: Date.now() + expireTTL,
                v: encodeConfig
            };

            localStorage.setItem( this.#sKey, JSON.stringify( item ) );
        }
    }

    /**
     * Retrieves the configuration map based on the tenant ID.
     * Caches the result to avoid redundant imports on subsequent calls.
     *
     * @async
     * @private
     * @param {number} tenantId - The tenant identifier (0, 2, or other)
     * @returns {Promise<Object>} The configuration map object
     * @throws {Error} If the import of configuration files fails
     */
    async #getConfigMap( tenantId ){
        // Return cached config map if it already exists
        if ( this.#configMap ){
            return this.#configMap;
        }

        const CONFIG = await import( "@/js/constants/baseConfig.js" ).then( m => m.default );
        const { APP_CONFIG, BASE } = CONFIG;

        if ( tenantId === 0 || tenantId === 2 ){
            if ( !this.#isUtilHelpersLoaded ){
                // Initialize utility helpers for base config
                await this.#getUtilHelpers();
            }

            // Import and cache the base configuration
            this.#configMap = this.#utilHelpers.mergeObjects( BASE, APP_CONFIG );
        } else {
            // Load OS-specific config for all other tenant IDs
            const app = "os";

            // Set map type to OS
            this.#mapType = app;
            const { generateConfResources, generateViewable } = this.#utilHelpers;

            if ( generateConfResources || generateViewable ){
                // Initialize utility helpers with OS-specific functions
                await this.#getUtilHelpers( app, true );
            }

            const { OS_CONFIG } = await import( "@/js/constants/baseOsConfig" );

            // Import and cache the OS configuration
            this.#configMap = this.#utilHelpers.mergeObjects( BASE, APP_CONFIG, OS_CONFIG );
        }

        // Return the cached configuration map
        return this.#configMap;
    }

    /**
     * Gets the enabled flags for various integrations and modules.
     * @param {Object} integrations - Object containing integration enable/disable flags
     * @param {Object} modules - Object containing module enable/disable flags
     * @returns {Object} Object with normalized boolean flags for each integration and module
     * @private
     */
    #getEnabledFlags( integrations, modules ){
        return {
            acuity: this.#utilHelpers.getBoolean( integrations.acuity_enabled ),
            appcues: this.#utilHelpers.getBoolean( integrations.appcues_enabled ),
            competitionsCommunity: this.#utilHelpers.getBoolean( modules.community_competitions ),
            competitionsProgram: this.#utilHelpers.getBoolean( modules.program_competitions ),
            googleads: this.#utilHelpers.getBoolean( integrations.googleads_enabled ),
            googleanalytics: this.#utilHelpers.getBoolean( integrations.googleanalytics_enabled ),
            googlemaps: this.#utilHelpers.getBoolean( integrations.googlemaps_enabled ),
            googletags: this.#utilHelpers.getBoolean( integrations.googletags_enabled ),
            hubspot: this.#utilHelpers.getBoolean( integrations.hubspot_enabled ),
            mixpanel: this.#utilHelpers.getBoolean( integrations.mixpanel_enabled ),
            posthog: this.#utilHelpers.getBoolean( integrations.posthog_enabled ),
            slack: this.#utilHelpers.getBoolean( integrations.slack_enabled )
        };
    }

    /**
     * Dynamically imports and initializes utility helper functions based on the application type.
     * Loads common utilities (strToBoolean, removeTrailingSlash) via dynamic imports,
     * then conditionally loads additional utilities specific to the app type.
     *
     * @async
     * @private
     * @param {string} appType - The application type that determines which utilities to load.
     *                          Expected values: "os" or other types.
     * @returns {Promise<void>} - Populates this.#utilHelpers with the appropriate utility functions.
     */
    async #getUtilHelpers( appType, reload = false ){
        if ( this.#isUtilHelpersLoaded && !reload ){
            return;
        }

        // Import utility functions in parallel: string-to-boolean converter and trailing slash remover
        const [ mergeObjects, removeEmptyObjValues, strToBoolean, removeTrailingSlash, cipher ] = await Promise.all( [
            import( "@/js/utils/tenantConfigHelpers.js" ).then( m => m.mergeObjects ),
            import( "@/js/utils/tenantConfigHelpers.js" ).then( m => m.removeEmptyObjValues ),
            import( "@/js/utils/boolean" ).then( m => m.strToBoolean ),
            import( "@/js/utils/strings" ).then( m => m.removeTrailingSlash ),
            import( "@/js/utils/jsonEncoder" ).then( m => ( { decode: m.decodeJson, encode: m.encodeJson } ) )

        ] );

        const baseHelpers = { cipher, getBoolean: strToBoolean, mergeObjects, removeEmptyObjValues, removeTrailingSlash };

        if ( appType === "os" ){ // Load OS-specific utilities if app type is "os"
            const { generateConfResources, generateViewable } = await import( "@/js/constants/baseOsConfig" );

            // Assign OS-specific utilities including resource and viewable generators
            this.#utilHelpers = { generateConfResources, generateViewable, ...baseHelpers };
        } else {

            // Assign base config utilities
            this.#utilHelpers = baseHelpers;
        }

        this.#isUtilHelpersLoaded = true;
    }

    // ---------- Non-OS CONFIG LOADER ---------- //
    /**
     * Loads and builds the complete application configuration for a non-os.
     *
     * This method fetches tenant configuration and persona data, then constructs a comprehensive
     * configuration object containing all necessary settings for the application including
     * authentication, integrations, UI settings, email templates, and vendor configurations.
     *
     * @async
     * @param {string|number} ACTIVE_TENANT_ID - The ID of the tenant to load configuration for
     * @param {boolean} [getPersona=true] - Flag indicating whether to fetch tenant persona data (default: true)
     * @returns {Promise<Object>}
     * @throws {Error} If tenant configuration cannot be loaded or Auth0Token is invalid
     */
    async #mainConfigLoader( ACTIVE_TENANT_ID, getPersona = false ){
        const config = {};
        const personaMap = [];

        // Check cache for existing config before making API calls
        const sConfig = await this.#cacheCheck( this.#sKey );

        if ( sConfig ){
            // If a valid cached config exists, return config
            return sConfig;
        }

        if ( !this.#isUtilHelpersLoaded ){
            await this.#getUtilHelpers();
        }

        // If getPersona is true, fetch both tenant configuration and persona data in parallel, otherwise fetch only tenant configuration
        if ( getPersona ){
            const [ reqConfig, reqPersonaMap ] = await Promise.all( [
                TenantConfigService.getTenantConfig(),
                TenantConfigService.getTenantPersona()
            ] );

            Object.assign( config, reqConfig );
            personaMap.push( ...reqPersonaMap );
        } else {
            const reqConfig = await TenantConfigService.getTenantConfig();

            Object.assign( config, reqConfig );
        }

        const {
            images,
            integrations,
            keyvault,
            modules,
            siteSettings,
            theme,
            tenant,
            tsunami
        } = config;

        const enabled = this.#getEnabledFlags( integrations, modules );
        const configMap = await this.#getConfigMap( ACTIVE_TENANT_ID );
        const OVERRIDE = {};
        // ---------- ROOT CONFIG ---------- //

        OVERRIDE.CONFIG = {
            ACTIVE: this.#utilHelpers.getBoolean( tenant.active ),
            S_KEY: this.#sKey
        };

        // ---------- DETAILS ---------- //

        OVERRIDE.DETAILS = {
            email: {
                company: tenant.tenantFullName || ""
            },
            emailAddress: siteSettings.return_address,
            emailAlias: keyvault.email.sentfrom || "",
            emailStyles: {
                logo: images.email || "",
                primaryColor: theme["primary-color"] || "rgb(7 152 240)",
                secondaryColor: theme["secondary-color"] || "rgb(2 50 79)"
            },
            fullName: tenant.tenantFullName || "",
            mixpanel: {
                key: enabled.mixpanel ? integrations.mixpanel_key : "",
                link: enabled.mixpanel ? integrations.mixpanel_link : ""
            },
            pageTitle: siteSettings.pageTitle || ""
        };

        // ---------- IMAGES ---------- //

        // OVERRIDE.IMAGES = {
        //     // announcementDefault: images.announcement || "",
        //     defaultPerkIcon: images.perk || "",
        //     email: images.email || "",
        //     // eventBanner: images.eventBanner || "",
        //     favicon: images.favicon || "",
        //     logo: images.main || "",
        //     navlogo: images.nav || ""
        // };

        // ---------- LINKS ---------- //

        OVERRIDE.LINKS = {

            APP_LINK: this.#utilHelpers.removeTrailingSlash( tsunami.loginUrl ) || ""
        };

        // ---------- VENDORS ---------- //

        OVERRIDE.VENDORS = {
            APPCUES: enabled.appcues ? integrations.appcues_token : "",
            GOOGLE: {
                ads: enabled.googleads ? integrations.googleads_token : "",
                analytics: enabled.googleanalytics ? integrations.googleanalytics_token : "",
                maps: enabled.googlemaps ? integrations.googlemaps_token : "",
                tags: enabled.googletags ? integrations.googletags_token : ""
            },
            HUBSPOT: {
                B_UNIT_ID: enabled.hubspot ? integrations.hubspot_business : "",
                PORTAL_KEY: enabled.hubspot ? integrations.hubspot_portal : ""
            },
            POSTHOG: enabled.posthog
                ? {
                    Dev: integrations.posthog_token,
                    host: integrations.posthog_host,
                    Prod: integrations.posthog_token,
                    QA: integrations.posthog_token,
                    Stage: integrations.posthog_token
                }
                : {}
        };

        // ---------- OTHER ---------- //

        OVERRIDE.ACUITY = enabled.acuity ? integrations.acuity_token : "";
        if ( !OVERRIDE.ACUITY ){
            delete OVERRIDE.ACUITY;
        }

        OVERRIDE.THEME = this.#utilHelpers.removeEmptyObjValues( theme );
        if ( personaMap?.length ){
            OVERRIDE.PERSONAS = personaMap;
        } else {
            delete OVERRIDE.PERSONAS;
        }

        const mergedConfig = this.#configMap = this.#utilHelpers.mergeObjects( {}, configMap, OVERRIDE );

        // Cache the config
        this.#cacheSet( mergedConfig );

        return mergedConfig;
    }

    // ---------- OS CONFIG LOADER ---------- //
    /**
     * Loads and builds the complete application configuration for os.
     *
     * This method fetches tenant configuration and persona data, then constructs a comprehensive
     * configuration object containing all necessary settings for the application including
     * authentication, integrations, UI settings, email templates, and vendor configurations.
     *
     * @async
     * @param {string|number} ACTIVE_TENANT_ID - The ID of the tenant to load configuration for
     * @param {boolean} [getPersona=true] - Flag indicating whether to fetch tenant persona data (default: true)
     * @returns {Promise<Object>}
     * @throws {Error} If tenant configuration cannot be loaded or Auth0Token is invalid
     */
    async #osConfigLoader( ACTIVE_TENANT_ID, getPersona = false ){
        const config = {};
        const personaMap = [];

        // Check cache for existing config before making API calls
        const sConfig = await this.#cacheCheck( this.#sKey );

        if ( sConfig ){
            // If a valid cached config exists, return config
            return sConfig;
        }

        // If getPersona is true, fetch both tenant configuration and persona data in parallel, otherwise fetch only tenant configuration
        if ( getPersona ){
            const [ reqConfig, reqPersonaMap ] = await Promise.all( [
                TenantConfigService.getTenantConfig(),
                TenantConfigService.getTenantPersona()
            ] );

            Object.assign( config, reqConfig );
            personaMap.push( ...reqPersonaMap );
        } else {
            const reqConfig = await TenantConfigService.getTenantConfig();

            Object.assign( config, reqConfig );
        }

        const {
            confluence,
            emailDictionary,
            images,
            integrations,
            keyvault,
            modules,
            services,
            siteSettings,
            tags,
            terminology,
            theme,
            tenant,
            tsunami,
            usermapping
        } = config;

        const enabled = this.#getEnabledFlags( integrations, modules );
        const configMap = await this.#getConfigMap( ACTIVE_TENANT_ID );
        const OVERRIDE = {};
        // ---------- ROOT CONFIG ---------- //

        OVERRIDE.CONFIG = {
            ACTIVE: this.#utilHelpers.getBoolean( tenant.active ),
            HAS_COMPETITIONS: enabled.competitionsCommunity,
            S_KEY: this.#sKey
        };

        // ---------- VIEWABLES & RESOURCES ---------- //

        if ( !this.#utilHelpers.generateViewable || this.#utilHelpers.generateConfResources ){
            await this.#getUtilHelpers( "os", true );
        }

        OVERRIDE.VIEWABLES = this.#utilHelpers.generateViewable( {
            ...integrations,
            ...modules,
            ENV: this.#env
        } );

        const resourceConfig = {
            ENV: this.#env,
            ovcSpaceKey: siteSettings.ovcSpaceKey,
            ovrCatPageId: siteSettings.ovrCatPageId,
            ovrSpaceKey: siteSettings.ovrSpaceKey,
            parentId: siteSettings.confluenceResourceParentId,
            samlIdp: confluence.saml_idp,
            spaceKey: siteSettings.confluenceResourceSpaceKey,
            templateId: siteSettings.confluenceResourceTemplateId
        };

        if ( Object.values( resourceConfig ).length ){
            OVERRIDE.RESOURCES = this.#utilHelpers.generateConfResources( resourceConfig );
        }

        // ---------- COMPETITIONS ---------- //

        OVERRIDE.COMPETITIONS = {
            community: enabled.competitionsCommunity,
            enabled:
                enabled.competitionsCommunity ||
                enabled.competitionsProgram ||
                false,
            programs: enabled.competitionsProgram
        };

        // ---------- DETAILS ---------- //

        OVERRIDE.DETAILS = {
            baseRole: {
                hasOtherBaseRole: !!usermapping?.base_role,
                selectedBaseRole: usermapping?.base_role || ""
            },
            email: {
                company: tenant.tenantFullName || "",
                meetingType: tenant.meeting_type || "",
                mentorLabel: terminology.meeting || "",
                perksName: terminology.perk || ""
            },
            emailAddress: siteSettings.return_address,
            emailAlias: keyvault.email.sentfrom || "",
            emailStyles: {
                logo: images.email || "",
                primaryColor: theme["primary-color"] || "rgb(7 152 240)",
                secondaryColor: theme["secondary-color"] || "rgb(2 50 79)"
            },
            eventTag: { tag: services.eventsTag },
            filterName: {
                name: tenant.tenantFullName || "",
                tenant: tenant.tenantName || "",
                value: services.eventsTag || ""
            },
            fullName: tenant.tenantFullName || "",
            mixpanel: {
                key: enabled.mixpanel ? integrations.mixpanel_key : "",
                link: enabled.mixpanel ? integrations.mixpanel_link : ""
            },
            pageTitle: siteSettings.pageTitle || "",
            perkTag: {
                name: tenant.tenantFullName,
                tag: services.perksTag
            },
            teams: {
                altText: {
                    team: terminology.team || "Team",
                    teams: terminology.team
                        ? `${ terminology.team }s`
                        : `Teams`
                }
            }
        };

        // ---------- IMAGES ---------- //

        OVERRIDE.IMAGES = {
            announcementDefault: images.announcement || "",
            defaultPerkIcon: images.perk || "",
            email: images.email || "",
            eventBanner: images.eventBanner || "",
            favicon: images.favicon || "",
            logo: images.main || "",
            navlogo: images.nav || "",
            slackBanner: integrations.slack_backgroundImage || ""
        };

        // ---------- LINKS ---------- //

        OVERRIDE.LINKS = {
            APP_LINK: this.#utilHelpers.removeTrailingSlash( tsunami.loginUrl ) || ""
        };

        // ---------- VENDORS ---------- //

        OVERRIDE.VENDORS = {
            APPCUES: enabled.appcues ? integrations.appcues_token : "",
            GOOGLE: {
                ads: enabled.googleads ? integrations.googleads_token : "",
                analytics: enabled.googleanalytics ? integrations.googleanalytics_token : "",
                maps: enabled.googlemaps ? integrations.googlemaps_token : ""
            },
            HUBSPOT: {
                B_UNIT_ID: enabled.hubspot ? integrations.hubspot_business : "",
                PORTAL_KEY: enabled.hubspot ? integrations.hubspot_portal : ""
            },
            POSTHOG: enabled.posthog
                ? {
                    Dev: integrations.posthog_token,
                    host: integrations.posthog_host,
                    Prod: integrations.posthog_token,
                    QA: integrations.posthog_token,
                    Stage: integrations.posthog_token
                }
                : {}
        };

        // ---------- OTHER ---------- //

        OVERRIDE.ACUITY = enabled.acuity ? integrations.acuity_token : "";
        OVERRIDE.COURSES = {
            OV_SMART_COURSES_SPACE: "OVC",
            SMART_COURSES_CONFIG: {
                tenantSpaceName: siteSettings.confluenceCoursesSpaceKey || ""
            }
        };

        OVERRIDE.EMAIL_DICTIONARY = {
            applicationSubmitSuccessBody:
                emailDictionary.applicationSubmitSuccess_body || "",
            applicationSubmitSuccessSubject:
                emailDictionary.applicationSubmitSuccess_subject || "",
            defaultInviteCompetitionSubject: {
                Admins: "",
                Competitors: emailDictionary.inviteApplicant_subject || "",
                Evaluators: emailDictionary.inviteEvaluators_body || ""
            },
            defaultInviteCompetitorBody:
                emailDictionary.inviteApplicant_body || "",
            defaultInviteEvaluatorBody:
                emailDictionary.inviteEvaluators_body || "",
            emailSignature: "",
            emailSubmitBcc: [],
            tenantPrettyName: tenant.tenantFullName || "",
            welcomeBodyMapping: {
                communityAdmin: emailDictionary.communityAdmin_body || "",
                existingProgramAdmin:
                    emailDictionary.programExistingAdmin_body || "",
                existingProgramUser:
                    emailDictionary.programExistingUser_body || "",
                judge: emailDictionary.inviteEvaluators_body || "",
                mentor: emailDictionary.programMentor_body || "",
                programAdmin: emailDictionary.programNewAdmin_body || "",
                startup: emailDictionary.communityTeamMember_body || "",
                user: emailDictionary.communityUser_body || ""
            }
        };

        OVERRIDE.SLACK = {
            accessKey: integrations.slack_token || "",
            iframe: integrations.slack_iframe || ""
        };

        OVERRIDE.THEME = this.#utilHelpers.removeEmptyObjValues( theme );
        OVERRIDE.TAGS = tags;
        OVERRIDE.PERSONAS = personaMap;

        const mergedConfig = this.#configMap = this.#utilHelpers.mergeObjects( {}, configMap, OVERRIDE );

        // Cache the config
        this.#cacheSet( mergedConfig );

        return mergedConfig;
    }

    /**
     * Computes and sets the tenant configuration key (`sKey`) based on the current host name.
     *
     * - Uses `import.meta.env.SITE_URL` if available, otherwise falls back to `window.location.href`.
     * - Extracts the hostname, strips the last segment (TLD), joins remaining segments with `_`, and
     *   replaces non-word characters with `_`.
     * - Appends `_tc` to form the final key.
     *
     * @private
     * @returns {void}
     */
    #setSKey(){
        const host = ( new URL( import.meta.env.SITE_URL || window.location.href )?.hostname || "" );
        const sKey = host.split( "." )
            .slice( 0, -1 )
            .join( "_" )
            .replace( /\W+/g, "_" );

        this.#sKey = `${ sKey }_tc${ import.meta.env.LIVE ? "" : "_dev" }`;
    }

}
const TenantConfig = new TenantConfigLoader();

await TenantConfig._init();

export default TenantConfig;